import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

export function AssistantSpotlight({
  selector,
  padding = 12,
  scrollOffset = 0,
  blockBackgroundInteraction = true
}) {
  const [rect, setRect] = useState(null)
  const targetRef = useRef(null)
  const hasEnsuredVisibilityRef = useRef(false)

  useLayoutEffect(() => {
    if (!selector) {
      targetRef.current = null
      setRect(null)
      return
    }

    const node = document.querySelector(selector)
    if (!node) return
    targetRef.current = node

    const updateRect = () => {
      const { top, left, width, height } = node.getBoundingClientRect()
      setRect({
        top: top - padding,
        left: left - padding,
        width: width + padding * 2,
        height: height + padding * 2,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight
      })
    }

    updateRect()
    const observer = new ResizeObserver(updateRect)
    observer.observe(node)
    window.addEventListener('scroll', updateRect, true)
    window.addEventListener('resize', updateRect, true)

    return () => {
      observer.disconnect()
      window.removeEventListener('scroll', updateRect, true)
      window.removeEventListener('resize', updateRect, true)
      targetRef.current = null
    }
  }, [selector, padding])

  useEffect(() => {
    hasEnsuredVisibilityRef.current = false
  }, [selector])

  useEffect(() => {
    if (!rect || !targetRef.current || hasEnsuredVisibilityRef.current) return

    const isOutOfView =
      rect.top < 0 ||
      rect.left < 0 ||
      rect.top + rect.height > window.innerHeight ||
      rect.left + rect.width > window.innerWidth

    if (isOutOfView) {
      const targetTop =
        targetRef.current.getBoundingClientRect().top +
        window.scrollY -
        scrollOffset

      window.scrollTo({
        top: Math.max(0, targetTop),
        behavior: 'smooth'
      })
    }

    hasEnsuredVisibilityRef.current = true
  }, [rect, scrollOffset])

  if (!rect) return null

  const { top, left, width, height, viewportWidth, viewportHeight } = rect

  const containerClassName = `fixed inset-0 z-20 ${
    blockBackgroundInteraction ? '' : 'pointer-events-none'
  }`
  const overlayPointerClass = blockBackgroundInteraction
    ? 'pointer-events-auto'
    : 'pointer-events-none'
  const overlayGuards = blockBackgroundInteraction
    ? {
        onPointerDown: (event) => event.preventDefault(),
        onClick: (event) => event.preventDefault()
      }
    : {}

  return createPortal(
    <div className={containerClassName}>
      {/* overlay segments */}
      <div
        className={`absolute left-0 right-0 top-0 bg-black/60 ${overlayPointerClass}`}
        style={{ height: Math.max(0, top) }}
        {...overlayGuards}
      />
      <div
        className={`absolute bottom-0 top-0 bg-black/60 ${overlayPointerClass}`}
        style={{ left: 0, width: Math.max(0, left), top, height }}
        {...overlayGuards}
      />
      <div
        className={`absolute bottom-0 top-0 bg-black/60 ${overlayPointerClass}`}
        style={{
          left: left + width,
          width: Math.max(0, viewportWidth - (left + width)),
          top,
          height
        }}
        {...overlayGuards}
      />
      <div
        className={`absolute left-0 right-0 bg-black/60 ${overlayPointerClass}`}
        style={{
          top: top + height,
          height: Math.max(0, viewportHeight - (top + height))
        }}
        {...overlayGuards}
      />

      {/* focus frame */}
      <div
        className='pointer-events-none absolute rounded-2xl border-2 border-white/80 shadow-[0_10px_40px_rgba(0,0,0,0.45)] transition-all duration-200'
        style={{ top, left, width, height, background: 'transparent' }}
      />
    </div>,
    document.body
  )
}
